import { MessageList } from './MessageList'
import { MessageInput } from './MessageInput'
import { UsageCounter } from './UsageCounter'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, FlaskConical, ExternalLink } from 'lucide-react'
import type { Message, MessageContent, Conversation } from '@/lib/api/chat'

interface ChatInterfaceProps {
  conversations: Conversation[]
  currentConversationId: string | null
  messages: Message[]
  streamingContent: MessageContent[]
  isStreaming: boolean
  isLoadingMessages: boolean
  error: string | null
  failedMessageId?: string | null
  onSendMessage: (message: string) => Promise<void>
  onRetryMessage?: (messageId: string) => void
  selectedModel: string | null
  onModelChange: (model: string | null) => void
}

export function ChatInterface({
  conversations,
  currentConversationId,
  messages,
  streamingContent,
  isStreaming,
  error,
  failedMessageId,
  onSendMessage,
  onRetryMessage,
  selectedModel,
  onModelChange,
}: ChatInterfaceProps) {
  // Find current conversation title
  const currentConversation = conversations.find((c) => c.uuid === currentConversationId)

  return (
    <div className="relative flex flex-col h-full bg-background">
      {/* Usage Counter Header - Compact */}
      {currentConversationId && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-border/50 bg-background/95 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-4">
            <UsageCounter
              conversationUuid={currentConversationId}
              conversationTitle={currentConversation?.title}
            />
          </div>
        </div>
      )}

      {/* Playground Banner - Show when no messages, more compact */}
      {currentConversationId && messages.length === 0 && (
        <div className="flex-shrink-0 px-4 py-3 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-b border-border/50">
          <div className="max-w-3xl mx-auto flex items-start gap-3">
            <FlaskConical className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <div className="space-y-0.5">
              <h3 className="text-xs font-medium">MCP Playground</h3>
              <p className="text-xs text-muted-foreground">
                Test tools here. For production, connect to{' '}
                <a
                  href="https://docs.ecosemantic.com/connection/overview/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-0.5"
                >
                  Claude or ChatGPT
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert - only show when no failed message (inline errors handle it) */}
      {error && !failedMessageId && (
        <Alert variant="destructive" className="mx-4 mt-2 flex-shrink-0">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Messages - Full height with padding for floating input */}
      <div className="flex-1 overflow-hidden">
        <MessageList
          messages={messages}
          streamingContent={streamingContent}
          isStreaming={isStreaming}
          failedMessageId={failedMessageId}
          errorMessage={error}
          onRetry={onRetryMessage}
        />
      </div>

      {/* Floating Input */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pb-4 bg-gradient-to-t from-background via-background to-transparent pointer-events-none">
        <div className="max-w-4xl mx-auto pointer-events-auto">
          <MessageInput
            onSend={onSendMessage}
            disabled={isStreaming}
            showSuggestions={messages.length === 0}
            selectedModel={selectedModel}
            onModelChange={onModelChange}
            placeholder={
              currentConversationId
                ? 'Ask about LCA data, search activities, or calculate impacts...'
                : 'Start a new conversation...'
            }
          />
        </div>
      </div>
    </div>
  )
}
